import { GRID_SIZE, LINE_HEIGHT } from '../constants.js'
import type { Block } from '../types.js'

export type Point = {
  x: number
  y: number
}

export type Rect = {
  x: number
  y: number
  width: number
  height: number
}

export type TextLayoutMeasurement = {
  heightPx: number
  lineCount: number
  maxLineWidthPx: number
}

export type TextLayoutOracle = {
  measureBlock(text: string, widthPx: number): TextLayoutMeasurement
}

export type BlockGeometry = {
  id: string
  block: Block
  rectPx: Rect
  rectUnits: Rect
  text: {
    lineCount: number
    maxLineWidthPx: number
  }
  handles: {
    start: Point
    end: Point
  }
}

export type DeoverlapGeometryEntry = {
  id: string
  top: number
  height: number
  isTarget: boolean
}

export type PlacementCandidate = {
  xUnits: number
  yUnits: number
  widthUnits: number
  heightUnits: number
}

export type PlacementOptions = {
  text?: string
  widthUnits?: number
  heightUnits?: number
  spacingUnits?: number
}

export type SpatialScope = {
  ids?: string[]
}

export type VerticalAxisDirection = 'above' | 'below'

export type SpaceGeometrySnapshot = {
  blocks: BlockGeometry[]
  byId(id: string): BlockGeometry | undefined
  blockAt(point: Point): BlockGeometry | undefined
  blocksInRect(rect: Rect): BlockGeometry[]
  deoverlapCollisionGroups(targetIds: Set<string>, opts: { snapY: (value: number) => number }): DeoverlapGeometryEntry[][]
  deoverlapStacks(targetIds: Set<string>, opts: { snapY: (value: number) => number }): DeoverlapGeometryEntry[][]
  makeRoomBelow(id: string, opts?: { spacingUnits?: number }): Map<string, number>
  openSlotBelow(id: string, opts?: PlacementOptions): PlacementCandidate | undefined
  readingOrder(scope?: SpatialScope): string[]
  verticalAxisSelection(id: string, direction: VerticalAxisDirection): string[]
}

type SnapshotOptions = {
  gridSize?: number
  lineHeightPx?: number
}

export function createSpaceGeometrySnapshot(
  blocks: Block[],
  oracle: TextLayoutOracle,
  opts: SnapshotOptions = {}
): SpaceGeometrySnapshot {
  const gridSize = opts.gridSize ?? GRID_SIZE
  const lineHeightPx = opts.lineHeightPx ?? LINE_HEIGHT
  const geometries = blocks.map(block => measureBlockGeometry(block, oracle, gridSize, lineHeightPx))
  const byIdMap = new Map(geometries.map(geometry => [geometry.id, geometry]))

  const measurePlacementHeightUnits = (text: string, widthUnits: number): number => {
    const widthPx = widthUnits * gridSize
    const measurement = oracle.measureBlock(text, widthPx)
    const heightPx = Math.max(lineHeightPx, measurement.heightPx)
    return Math.max(1, Math.ceil(heightPx / gridSize))
  }

  return {
    blocks: geometries,
    byId(id: string): BlockGeometry | undefined {
      return byIdMap.get(id)
    },
    blockAt(point: Point): BlockGeometry | undefined {
      for (let i = geometries.length - 1; i >= 0; i--) {
        const geometry = geometries[i]
        if (geometry && containsPoint(geometry.rectPx, point)) {
          return geometry
        }
      }
      return undefined
    },
    blocksInRect(rect: Rect): BlockGeometry[] {
      return geometries.filter(geometry => rectsIntersect(geometry.rectPx, rect))
    },
    deoverlapCollisionGroups(
      targetIds: Set<string>,
      deoverlapOpts: { snapY: (value: number) => number }
    ): DeoverlapGeometryEntry[][] {
      return buildCollisionGroups(geometries)
        .filter(group => group.some(geometry => targetIds.has(geometry.id)))
        .map(group => group.map(geometryToDeoverlapEntry(targetIds, deoverlapOpts.snapY)))
    },
    deoverlapStacks(
      targetIds: Set<string>,
      deoverlapOpts: { snapY: (value: number) => number }
    ): DeoverlapGeometryEntry[][] {
      const stacks: BlockGeometry[][] = []
      const ordered = [...geometries].sort((a, b) => a.rectUnits.x - b.rectUnits.x || a.id.localeCompare(b.id))

      ordered.forEach(geometry => {
        const matchingStacks = stacks.filter(stack => stack.some(member => horizontalRangesOverlap(
          member.rectUnits.x,
          member.rectUnits.width,
          geometry.rectUnits.x,
          geometry.rectUnits.width
        )))

        if (!matchingStacks.length) {
          stacks.push([geometry])
          return
        }

        const targetStack = matchingStacks[0]
        targetStack.push(geometry)
        matchingStacks.slice(1).forEach(stack => {
          targetStack.push(...stack)
          stacks.splice(stacks.indexOf(stack), 1)
        })
      })

      return stacks.map(stack => stack.map(geometryToDeoverlapEntry(targetIds, deoverlapOpts.snapY)))
    },
    makeRoomBelow(id: string, makeRoomOpts: { spacingUnits?: number } = {}): Map<string, number> {
      const source = byIdMap.get(id)
      const updates = new Map<string, number>()
      if (!source) return updates

      const spacingUnits = Math.max(0, makeRoomOpts.spacingUnits ?? 0)
      let occupiedBottom = source.rectUnits.y + source.rectUnits.height + spacingUnits
      const stack = geometries
        .filter(geometry => geometry.id !== id)
        .filter(geometry => geometry.rectUnits.y >= source.rectUnits.y)
        .filter(geometry => horizontalRangesOverlap(
          source.rectUnits.x,
          source.rectUnits.width,
          geometry.rectUnits.x,
          geometry.rectUnits.width
        ))
        .sort((a, b) => a.rectUnits.y - b.rectUnits.y || a.id.localeCompare(b.id))

      for (const geometry of stack) {
        if (geometry.rectUnits.y >= occupiedBottom) break
        const nextY = snapEvenUp(occupiedBottom)
        updates.set(geometry.id, nextY)
        occupiedBottom = nextY + geometry.rectUnits.height + spacingUnits
      }

      return updates
    },
    openSlotBelow(id: string, placementOpts: PlacementOptions = {}): PlacementCandidate | undefined {
      const anchor = byIdMap.get(id)
      if (!anchor) return undefined

      const spacingUnits = Math.max(0, placementOpts.spacingUnits ?? 0)
      const widthUnits = Math.max(1, Math.round(placementOpts.widthUnits ?? anchor.rectUnits.width))
      const heightUnits = Math.max(
        1,
        Math.round(
          placementOpts.heightUnits ??
          measurePlacementHeightUnits(placementOpts.text ?? '', widthUnits)
        )
      )
      const xUnits = Math.round(anchor.rectUnits.x)
      let yUnits = snapEvenUp(anchor.rectUnits.y + anchor.rectUnits.height + spacingUnits)

      const blockers = geometries
        .filter(geometry => geometry.id !== id)
        .filter(geometry => horizontalRangesOverlap(xUnits, widthUnits, geometry.rectUnits.x, geometry.rectUnits.width))
        .sort((a, b) => a.rectUnits.y - b.rectUnits.y || a.id.localeCompare(b.id))

      for (let guard = 0; guard <= blockers.length; guard++) {
        const candidate = { x: xUnits, y: yUnits, width: widthUnits, height: heightUnits }
        const blocker = blockers.find(geometry => rectsIntersect(candidate, geometry.rectUnits))
        if (!blocker) {
          return { xUnits, yUnits, widthUnits, heightUnits }
        }
        yUnits = snapEvenUp(blocker.rectUnits.y + blocker.rectUnits.height + spacingUnits)
      }

      return undefined
    },
    readingOrder(scope?: SpatialScope): string[] {
      const scopeIds = scope?.ids ? new Set(scope.ids) : undefined
      return geometries
        .filter(geometry => !scopeIds || scopeIds.has(geometry.id))
        .sort((a, b) => {
          return a.rectUnits.x - b.rectUnits.x ||
            a.rectUnits.y - b.rectUnits.y ||
            a.id.localeCompare(b.id)
        })
        .map(geometry => geometry.id)
    },
    verticalAxisSelection(id: string, direction: VerticalAxisDirection): string[] {
      const base = byIdMap.get(id)
      if (!base) return []

      const candidates = geometries
        .filter(geometry => geometry.id !== id)
        .filter(geometry => horizontalRangesOverlap(
          base.rectUnits.x,
          base.rectUnits.width,
          geometry.rectUnits.x,
          geometry.rectUnits.width
        ))
        .filter(geometry => {
          if (direction === 'above') {
            return geometry.rectUnits.y + geometry.rectUnits.height <= base.rectUnits.y
          }
          return geometry.rectUnits.y >= base.rectUnits.y + base.rectUnits.height
        })
        .sort((a, b) => {
          if (direction === 'above') {
            return (b.rectUnits.y + b.rectUnits.height) - (a.rectUnits.y + a.rectUnits.height) ||
              a.rectUnits.x - b.rectUnits.x ||
              a.id.localeCompare(b.id)
          }
          return a.rectUnits.y - b.rectUnits.y ||
            a.rectUnits.x - b.rectUnits.x ||
            a.id.localeCompare(b.id)
        })
        .map(geometry => geometry.id)

      return [id, ...candidates]
    }
  }
}

function measureBlockGeometry(
  block: Block,
  oracle: TextLayoutOracle,
  gridSize: number,
  lineHeightPx: number
): BlockGeometry {
  const widthPx = block.xyzw[3] * gridSize
  const measurement = oracle.measureBlock(block.text, widthPx)
  const heightPx = Math.max(lineHeightPx, measurement.heightPx)
  const heightUnits = Math.max(1, Math.ceil(heightPx / gridSize))
  const rectPx = {
    x: block.xyzw[0] * gridSize,
    y: block.xyzw[1] * gridSize,
    width: widthPx,
    height: heightPx,
  }
  const rectUnits = {
    x: block.xyzw[0],
    y: block.xyzw[1],
    width: block.xyzw[3],
    height: heightUnits,
  }

  return {
    id: block.ided[0],
    block,
    rectPx,
    rectUnits,
    text: {
      lineCount: Math.max(1, measurement.lineCount),
      maxLineWidthPx: measurement.maxLineWidthPx,
    },
    handles: {
      start: { x: rectPx.x, y: rectPx.y },
      end: { x: rectPx.x + rectPx.width, y: rectPx.y + rectPx.height },
    },
  }
}

function geometryToDeoverlapEntry(
  targetIds: Set<string>,
  snapY: (value: number) => number
): (geometry: BlockGeometry) => DeoverlapGeometryEntry {
  return geometry => ({
    id: geometry.id,
    top: snapY(geometry.rectUnits.y),
    height: geometry.rectUnits.height,
    isTarget: targetIds.has(geometry.id)
  })
}

function buildCollisionGroups(geometries: BlockGeometry[]): BlockGeometry[][] {
  const groups: BlockGeometry[][] = []
  const visited = new Set<string>()

  geometries.forEach(geometry => {
    if (visited.has(geometry.id)) return
    const group: BlockGeometry[] = []
    const queue = [geometry]
    visited.add(geometry.id)

    while (queue.length) {
      const current = queue.shift()
      if (!current) continue
      group.push(current)

      geometries.forEach(candidate => {
        if (visited.has(candidate.id)) return
        if (!rectsIntersect(current.rectUnits, candidate.rectUnits)) return
        visited.add(candidate.id)
        queue.push(candidate)
      })
    }

    if (group.length > 1) {
      groups.push(group)
    }
  })

  return groups
}

function snapEvenUp(value: number): number {
  return Math.ceil(value / 2) * 2
}

function containsPoint(rect: Rect, point: Point): boolean {
  return point.x >= rect.x &&
    point.x <= rect.x + rect.width &&
    point.y >= rect.y &&
    point.y <= rect.y + rect.height
}

function rectsIntersect(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
}

function horizontalRangesOverlap(aX: number, aWidth: number, bX: number, bWidth: number): boolean {
  return aX < bX + bWidth && aX + aWidth > bX
}
